import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useNotification } from '../contexts/NotificationContext';
import { mapErrorKey } from '../utils/errorMapping';
import { Button } from 'baseui/button';
import { Input } from 'baseui/input';
import { FormControl } from 'baseui/form-control';
import { HeadingLarge, ParagraphMedium } from 'baseui/typography';
import {
  PageContainer,
  SectionContainer,
  FlexColumnCentered
} from '../styles/controllerPageStyles';
import {
  ErrorMessage,
  AuthButtonContainer,
  AuthLinkContainer
} from '../styles/authPageStyles';
import PageFooter from '../components/common/PageFooter';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { resetPasswordForEmail } = useAuth(); // Need to expose this from AuthContext
  const { t } = useLocale();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const { error } = await resetPasswordForEmail(email);
      if (error) {
        throw error;
      }
      setMessage(t('pages.forgotPassword.success'));
      showNotification(t('pages.forgotPassword.success'), 'success');
    } catch (err) {
      // Simple error handling
      const errorKey = err.message || 'UNKNOWN_ERROR';
      const clientKey = mapErrorKey(errorKey);

      // If the error is unknown (not mapped), display the raw message for debugging
      const displayError = clientKey === 'errors.unknown' ? errorKey : t(clientKey);

      setError(displayError);
      showNotification(displayError, 'error');
    }
    setLoading(false);
  };

  return (
    <PageContainer>
      <FlexColumnCentered>
        <HeadingLarge>{t('pages.forgotPassword.title')}</HeadingLarge>
      </FlexColumnCentered>
      <SectionContainer>
        <ParagraphMedium style={{ marginBottom: '24px' }}>
          {t('pages.forgotPassword.description')}
        </ParagraphMedium>
        {message && (
          <div style={{ color: 'green', marginBottom: '16px', fontWeight: 'bold' }}>
            {message}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <FormControl label={() => t('common.emailLabel')}>
            <Input
              id="email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </FormControl>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <AuthButtonContainer>
            <Button type="submit" isLoading={loading}>
              {t('pages.forgotPassword.button')}
            </Button>
          </AuthButtonContainer>
        </form>
        <AuthLinkContainer>
          <Button onClick={() => navigate('/login')} kind="tertiary" size="compact">
            {t('pages.forgotPassword.backToLogin')}
          </Button>
        </AuthLinkContainer>
      </SectionContainer>
      <PageFooter />
    </PageContainer>
  );
};

export default ForgotPasswordPage;
